import React, { useRef, useState } from "react";
import CodeMirror from "@uiw/react-codemirror";
import { json } from "@codemirror/lang-json";
import { yaml } from "@codemirror/lang-yaml";
import { Container, Header, Button, Segment, Message, Input, Select, Icon } from "semantic-ui-react";
import * as YAML from "yaml";
import { isJson, extractLineColumn, jumpToPosition, convertContentType } from "utils/editorUtils";

const options = [
  { key: 2, text: "Tab (2 spaces)", value: 2 },
  { key: 4, text: "Tab (4 spaces)", value: 4 },
];

function JsonYamlEditor(props) {
  const [context, setContext] = useState({ hasErrors: false });
  const [indention, setIndention] = useState(2);
  const [response, setResponse] = useState({});
  const editorRef = useRef(null);
  const textAreaRef = useRef(null);

  const validateContent = async () => {
    try {
      isJson(context.content) ? JSON.parse(context.content) : YAML.parse(context.content);
      const contentType = isJson(context.content) ? "JSON" : "YAML";
      setResponse({ error: false, message: contentType + " is valid!" });
    } catch (e) {
      setResponse({ error: true, message: e.message });
      const position = extractLineColumn(e.message);
      jumpToPositionInEditor(...position.split(":").map(Number));
    }
  };

  const copyToClipboard = (e) => {
    textAreaRef.current.style.visibility = "visible";
    textAreaRef.current.select();
    document.execCommand("copy");
    e.target.focus();
    textAreaRef.current.style.visibility = "hidden";
  };

  const updateContext = (content) => {
    const newContext = { content: content };
    try {
      isJson(content) ? JSON.parse(content) : YAML.parse(content);
      newContext.hasErrors = false;
      setResponse({});
    } catch (e) {
      newContext.hasErrors = true;
      newContext.error = e.message;
      setResponse({ error: true, message: e.message });
    }
    setContext(newContext);
  };

  const convertContent = () => {
    const newContent = convertContentType(context.content);
    setContext({ hasErrors: false, content: newContent });
    setResponse({});
  };

  const compressContent = () => {
    const compressed = JSON.stringify(JSON.parse(context.content));
    setContext({ hasErrors: false, content: compressed });
  };

  const prettifyContent = () => {
    const prettified = isJson(context.content)
      ? JSON.stringify(JSON.parse(context.content), null, indention)
      : YAML.stringify(YAML.parse(context.content), null, { indent: indention });
    setContext({ hasErrors: false, content: prettified });
  };

  const clear = () => {
    setContext({ hasErrors: false, content: "" });
    setResponse({});
  };

  const handleMessage = () => {
    if (context.hasErrors) {
      const position = extractLineColumn(context.error);
      jumpToPositionInEditor(...position.split(":").map(Number));
    } else {
      setResponse({});
    }
  };

  const jumpToPositionInEditor = (lineNumber, column) => {
    try {
      jumpToPosition(lineNumber, column, editorRef);
    } catch (e) {
      console.log(e.message);
    }
  };

  return (
    <Container className="json-yaml-editor-container">
      <Segment>
        <Header>JSON/YAML Editor</Header>
        <Header.Subheader style={{ marginBottom: "1em" }}>
          This web tool offers a user-friendly interface for validating, editing, and formating documents in JSON or
          YAML format.
        </Header.Subheader>
        <textarea
          style={{ width: "0px", height: "0px", visibility: "hidden" }}
          ref={textAreaRef}
          value={context.content}
        />
        <Container className="buttons-container">
          <Button color="blue" disabled={!context.content} onClick={validateContent}>
            Validate
          </Button>
          <Button negative disabled={!context.content} onClick={() => clear()}>
            Clear
          </Button>
          <Button color="blue" onClick={convertContent} disabled={!context.content || context.hasErrors}>
            {`Convert to ${isJson(context.content) ? "YAML" : "JSON"}`}
          </Button>
          <Button color="blue" onClick={copyToClipboard} style={{ marginRight: "10px" }} disabled={!context.content}>
            <Icon name="copy" /> Copy
          </Button>
          <Input type="text">
            <Select
              compact
              options={options}
              defaultValue={2}
              onChange={(e, val) => setIndention(val.value)}
              className="tab-width-select"
            />
            <Button
              color="blue"
              onClick={() => prettifyContent()}
              disabled={!context.content || context.hasErrors}
              className="tab-width-button"
            >
              Prettify
            </Button>
          </Input>
          <Button
            color="blue"
            onClick={() => compressContent()}
            disabled={!context.content || context.hasErrors || !isJson(context.content)}
          >
            Compress
          </Button>
        </Container>
      </Segment>
      {response?.message && (
        <Message
          className="json-editor-message"
          error={response.error}
          header={response.message}
          success={!response.error}
          onDismiss={() => setResponse({})}
          data-testid="message"
          onClick={() => handleMessage()}
          style={{ cursor: "pointer" }}
        ></Message>
      )}

      <CodeMirror
        style={{ width: "100%", height: "100%", border: "solid 2px gray", borderRadius: "4px" }}
        theme={"dark"}
        width="relative"
        maxWidth="100%"
        height="100%"
        value={context.content}
        onChange={(value, viewUpdate) => updateContext(value)}
        extensions={[isJson(context.content) ? json() : yaml()]}
        onCreateEditor={(view) => (editorRef.current = { view })}
      />
    </Container>
  );
}

export default JsonYamlEditor;
